/**
 * Project-context contract for the customer portal.
 *
 * The `?project=<id>` query parameter is the single carrier of the selected
 * project. Every module falls in exactly one scope so the header can never
 * imply a filter the page does not apply:
 *
 * - `filtered`: the module lists records and applies the project as a filter
 *   (no project means every authorized project).
 * - `selected`: the module is a single-project workspace that resolves its own
 *   project from the parameter (it picks a default when absent).
 * - `account`: account-wide data; the parameter is ignored and never carried.
 */
export const PORTAL_PROJECT_PARAM = "project";

export type PortalProjectScope = "filtered" | "selected" | "account";

const FILTERED_MODULES = ["/tasks", "/payments", "/documents", "/discussions"] as const;
const SELECTED_MODULES = ["/files", "/commercial", "/roadmap"] as const;

/** Project ids are opaque server identifiers; anything else is malformed input. */
const PROJECT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

const isModulePath = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

export function portalProjectScope(pathname: string): PortalProjectScope {
  if (FILTERED_MODULES.some((base) => isModulePath(pathname, base))) return "filtered";
  if (pathname === "/" || SELECTED_MODULES.some((base) => isModulePath(pathname, base))) return "selected";
  return "account";
}

export type RequestedPortalProject =
  | { kind: "none" }
  | { kind: "malformed" }
  | { kind: "valid"; id: string };

/** Validates the raw query value without trimming it into a different identifier. */
export function parseRequestedPortalProject(search: URLSearchParams): RequestedPortalProject {
  const values = search.getAll(PORTAL_PROJECT_PARAM);
  if (values.length === 0) return { kind: "none" };
  if (values.length > 1) return { kind: "malformed" };
  return PROJECT_ID_PATTERN.test(values[0]) ? { kind: "valid", id: values[0] } : { kind: "malformed" };
}

/** Appends the project to an app path, preserving any query already present. */
export function withPortalProject(path: string, projectId: string | null | undefined): string {
  if (!projectId) return path;
  const [base, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  params.set(PORTAL_PROJECT_PARAM, projectId);
  return `${base}?${params.toString()}`;
}

/** Identifiers that belong to one project's own records and mean nothing in another project. */
export const PROJECT_OWNED_PARAMS = ["folder", "tag"] as const;

/**
 * Returns the params for a project change in one transition: sets or clears the
 * project and drops the page cursor, which is meaningless for a different record
 * set. When the project actually changes, project-owned ids (file folder and tag)
 * go with it. Search, sort and status preferences keep their meaning across
 * projects and survive.
 */
export function switchPortalProject(current: URLSearchParams, projectId: string | null): URLSearchParams {
  const next = new URLSearchParams(current);
  if ((current.get(PORTAL_PROJECT_PARAM) ?? null) !== (projectId || null)) {
    for (const key of PROJECT_OWNED_PARAMS) next.delete(key);
  }
  if (projectId) next.set(PORTAL_PROJECT_PARAM, projectId);
  else next.delete(PORTAL_PROJECT_PARAM);
  next.delete("page");
  return next;
}
